import { useEffect, useMemo } from "react";

interface PageMeta {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  jsonLd?: Record<string, any>;
}

function setMetaTag(property: string, content: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  let tag = document.querySelector(`meta[${attr}="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

const JSON_LD_ID = "page-jsonld";

export function usePageMeta(meta: PageMeta) {
  const jsonLdStr = useMemo(
    () => (meta.jsonLd ? JSON.stringify(meta.jsonLd) : null),
    [meta.jsonLd],
  );

  useEffect(() => {
    const prevTitle = document.title;

    document.title = meta.title;
    setMetaTag("description", meta.description);
    setMetaTag("og:title", meta.ogTitle || meta.title, true);
    setMetaTag("og:description", meta.ogDescription || meta.description, true);
    setMetaTag("og:type", meta.ogType || "website", true);
    if (meta.ogUrl) {
      setMetaTag("og:url", meta.ogUrl, true);
      setMetaTag("twitter:url", meta.ogUrl);
    }
    if (meta.ogImage) {
      setMetaTag("og:image", meta.ogImage, true);
      setMetaTag("twitter:image", meta.ogImage);
    }
    setMetaTag("twitter:card", meta.twitterCard || "summary_large_image");
    setMetaTag("twitter:title", meta.ogTitle || meta.title);
    setMetaTag("twitter:description", meta.ogDescription || meta.description);

    if (jsonLdStr) {
      let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = JSON_LD_ID;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = jsonLdStr;
    } else {
      const existing = document.getElementById(JSON_LD_ID);
      if (existing) existing.remove();
    }

    return () => {
      document.title = prevTitle;
      const existing = document.getElementById(JSON_LD_ID);
      if (existing) existing.remove();
    };
  }, [meta.title, meta.description, meta.ogTitle, meta.ogDescription, meta.ogType, meta.ogUrl, meta.ogImage, meta.twitterCard, jsonLdStr]);
}
